import { uniqueId } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  ArrowClockwise,
  ArrowsAngleContract,
  ArrowsAngleExpand,
  Check,
  EyeSlash,
  Files,
  Folder2,
  Fullscreen,
  Plus,
  Trash
} from 'react-bootstrap-icons';
import { LocalizeContext } from 'react-locale-language';
import { toast } from 'react-toastify';
import GoogleDriveIcon from '../../assets/GoogleDriveIcon.png';
import { deleteFileFromServer, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { scrollToEnd, isCustomer } from '../../helpers/global';
import { isRtl } from '../../helpers/localization';
import useAppChoices from '../../hooks/useAppChoices';
import useAuth from '../../hooks/useAuth';
import useDebouncedEffect from '../../hooks/useDebouncedEffect';
import AlertModal from '../common/AlertModal';
import FileViewer from '../common/FileViewer';
import FullscreenModal from '../common/FullscreenModal';
import DataRow from './ApartmentDataRow';
import SectionRow from './ApartmentSectionRow';
import CustomerSelectModal from './CustomerSelectModal';
import { getPinuiBinuiIrrFields } from './constants';
import { combinedIRRFormula, computeTargetPrice } from './goalSeekerPinuiBinui';
import { getInitialValues, getParsedValues, getPercentageValues, updateApartmentValues } from './helpers';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Calculator as CalculatorIcon,
  ExpandIcon,
  FoldHorizontalIcon,
  PlusIcon,
  Save,
  ShrinkIcon,
  UnfoldHorizontalIcon
} from 'lucide-react';
import { Button as ButtonTW } from '../ui/button';

const getServerFieldsOfAppartments = (app, isCollapsed, isArchived) => {
  const {
    targetIrr,
    inputPrice,
    askedPrice,
    areaInSquareMeters,
    renovationCost,
    valueAddedByRenovation,
    appraisedValue,
    maximalMortgageAvailable,
    onlyApartmentWhileBuying,
    onlyApartmentWhileSelling,
    mortgagePercentage,
    expectedAppreciation,
    realEstateHoldingPeriodInYears,
    timeToDeliveryAfterBuyingInMonths,
    monthlyRent,
    rentDuringContruction,
    rentPostConstruction,
    projectStage,
    apartmentSizeAfterReconstruction,
    apartmentSizeAfterReconstructionLikely,
    valueOfApartmentToday,
    estimatedTimeForDemolition,
    estimatedTimeForFinishingContruction,
    irrInNoReconstruction,
    realtorCostsInPercentageTermsPreVat,
    vat,
    relatedCostsLawyerAppraiserEtc,
    mortgageInterest,
    mortgageTimeToMaturityInMonths,
    taxOnRent,
    rentIncrease,
    files,
    additionalComments,
    address,
    apartmentFloorNumber,
    apartmentSize,
    city,

    numberOfRooms,
    rating,
    realtorSellerName,
    wanterPricePerSQM,

    signaturePercentage,

    developerCompany,

    physicalCondition,

    importedFrom,
    requiredCashForDeal,
    developerLoan,
    linearPayment,
    developerCompanyScale,
    buyingDecisionScale,
    largeRenovation,
    earlyDeal,
    noElevator,
    complicatedDeals,
    mixedPopulation,
    cashflow
  } = app;

  return {
    targetIrr,
    inputPrice,
    askedPrice,
    areaInSquareMeters,
    renovationCost,
    valueAddedByRenovation,
    appraisedValue,
    maximalMortgageAvailable,
    onlyApartmentWhileBuying,
    onlyApartmentWhileSelling,
    mortgagePercentage,
    expectedAppreciation,
    realEstateHoldingPeriodInYears,
    timeToDeliveryAfterBuyingInMonths,
    monthlyRent,
    realtorCostsInPercentageTermsPreVat,
    vat,
    relatedCostsLawyerAppraiserEtc,
    mortgageInterest,
    mortgageTimeToMaturityInMonths,
    taxOnRent,
    rentIncrease,
    files,
    additionalComments,
    address,
    apartmentFloorNumber,
    apartmentSize,
    city,

    numberOfRooms,
    rating,
    realtorSellerName,
    wanterPricePerSQM,

    rentDuringContruction,
    rentPostConstruction,
    projectStage,
    apartmentSizeAfterReconstruction,
    apartmentSizeAfterReconstructionLikely,
    valueOfApartmentToday,
    estimatedTimeForDemolition,
    estimatedTimeForFinishingContruction,
    irrInNoReconstruction,
    isCollapsed,
    isArchived,

    signaturePercentage,

    developerCompany,

    physicalCondition,

    importedFrom: importedFrom?._id,
    requiredCashForDeal,
    developerLoan,
    linearPayment,
    developerCompanyScale,
    buyingDecisionScale,
    largeRenovation,
    earlyDeal,
    noElevator,
    complicatedDeals,
    mixedPopulation,
    cashflow
  };
};

const Calculator = ({
  customer,
  readOnlyMode,
  selectMode,
  addApartmentMode,
  onChange,
  fullScreenView,
  setFullScreenView,
  showImportApartment,
  relatedApartments,
  appartments,
  onImportApartment,
  isRtlLanguage,
  translate,
  updatingAppartments,
  onAddAppartmentClick,
  onSaveAppartmentsClick,
  tableContainerRef,
  calculatorHeight,
  apparmentsToShow,
  collapsedApartments,
  totalFieldsCount,
  onInputValueChange,
  onCollapseApartmentClick,
  handleSelectAppartment,
  onDeleteAppartmentClick,
  onArchiveAppartmentClick,
  onCopyApartmentClick,
  onResetApartmentClick,
  irrFields,
  allFields,
  archiveAppartmentMeta,
  setArchiveAppartmentMeta,
  onArchiveAppartment,
  apartmentCopyModalMeta,
  setApartmentCopyModalMeta,
  onApartmentCopySubmit,
  onViewFilesClick,
  viewingFilesOfAppartment,
  setViewingFilesOfAppartment,
  fileUploadModalMeta,
  setFileUploadMeta,
  deleteFileModalMeta,
  setDeleteFileModalMeta,
  deleteFile,
  onFileDeleteClick,
  onFileSaveClick,
  allowNote,
  notes,
  onFieldInfoClick,
  user
}) => {
  return (
    <>
      <Container fluid className={'calculator h-100 text-start p-0'}>
        <Card className={`overflow-auto p-2 rounded-0 border-0 `}>
          {showImportApartment && (
            <div className="px-3 mb-2">
              <h6 className="mid mb-1 fw-bold">You may want to import the following apartments to the calculator:</h6>
              <div className="d-flex overflow-auto ">
                {relatedApartments
                  .filter(app => !appartments.map(a => a.importedFrom?._id).includes(app._id))
                  .map(app => (
                    <Badge key={app._id} className="mx-1 hover mt-1" onClick={() => onImportApartment(app)}>
                      {app.address ? `${app.address}-` : ''}
                      {app.inputPrice}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
          {!addApartmentMode && (
            <Card.Header className="bg-white mb-2 border-0 ">
              <div className="d-flex">
                <div
                  className={`flex-grow-1 d-flex align-items-center gap-2 font-bold text-${
                    isRtlLanguage ? 'end' : 'start'
                  }`}
                >
                  <CalculatorIcon size={16} />
                  {translate('pinui_binui')}{' '}
                  {fullScreenView ? (
                    <ShrinkIcon size={16} onClick={() => setFullScreenView(false)} cursor="pointer" />
                  ) : (
                    <ExpandIcon size={16} onClick={() => setFullScreenView(true)} cursor="pointer" />
                  )}
                </div>
                <div>
                  {!readOnlyMode && !selectMode && (
                    <>
                      <ButtonTW
                        disabled={updatingAppartments}
                        variant="outline"
                        size="sm"
                        className="px-2 shadow-none border border-light-blue py-0 text-light-blue rounded-lg hover:text-white"
                        onClick={onAddAppartmentClick}
                      >
                        <PlusIcon size={10} />
                        <span className="align-middle">{translate('add')}</span>
                      </ButtonTW>
                      {customer && (
                        <ButtonTW
                          disabled={updatingAppartments}
                          variant="outline"
                          size="sm"
                          className="px-2 shadow-none border py-0 border-success text-success rounded-lg hover:bg-[rgb(var(--bs-success-rgb))] hover:text-white"
                          onClick={() => onSaveAppartmentsClick()}
                        >
                          <Save size={10} />
                          <span className="align-middle mx-1">{translate('save')}</span>
                        </ButtonTW>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card.Header>
          )}
          <Card.Body
            ref={tableContainerRef}
            className="pt-0 pb-4 scrollbar-visible overflow-scroll px-4"
            style={{ height: calculatorHeight }}
          >
            <Table
              className={`overflow-auto border border-gray-400  px-2 ${!addApartmentMode ? 'calculator-table' : ''}`}
            >
              {!addApartmentMode && (
                <TableHeader className="border-0">
                  <TableRow className="border-0">
                    <TableHead className="font-bold ps-4 bg-white" />
                    {apparmentsToShow?.map((app, index) => (
                      <TableHead key={app.id} className="text-right py-1 px-1 bg-white">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1 items-center justify-between w-full">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                collapsedApartments.includes(app.id) ? (
                                  <Tooltip id={`tooltip-collapsed-address-${app.id}`}>
                                    {app.address || translate('no_address')}
                                  </Tooltip>
                                ) : (
                                  <></>
                                )
                              }
                            >
                              <span>
                                <ButtonTW
                                  variant="outline"
                                  size="sm"
                                  className="px-1 m-0 py-0 border-0 shadow-none bg-transparent"
                                  onClick={() => onCollapseApartmentClick(app.id)}
                                >
                                  {collapsedApartments.includes(app.id) ? (
                                    <UnfoldHorizontalIcon size={12} />
                                  ) : (
                                    <FoldHorizontalIcon size={12} />
                                  )}
                                </ButtonTW>
                              </span>
                            </OverlayTrigger>
                            <div>
                              {selectMode && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className="px-1 py-0 mx-1"
                                  onClick={() => handleSelectAppartment(app)}
                                >
                                  Select
                                </Button>
                              )}
                              {!collapsedApartments.includes(app.id) && !readOnlyMode && !selectMode && (
                                <>
                                  {customer && (
                                    <>
                                      {!isCustomer(user.role) && (
                                        <>
                                          <Button
                                            size="sm"
                                            className="px-1 py-0 mx-1 bg-primary-light text-black border-none hover:bg-[rgb(var(--bs-primary-rgb))] hover:text-white"
                                            onClick={() => onArchiveAppartmentClick(app)}
                                          >
                                            <EyeSlash size={12} />
                                          </Button>
                                          <Button
                                            size="sm"
                                            className="px-1 py-0 mx-1 bg-primary-light text-black border-none hover:bg-[rgb(var(--bs-primary-rgb))] hover:text-white"
                                            onClick={() => onCopyApartmentClick(app.id)}
                                          >
                                            <Files size={12} />
                                          </Button>
                                        </>
                                      )}
                                      <Button
                                        size="sm"
                                        className="px-1 py-0 mx-1 bg-primary-light text-black border-none hover:bg-[rgb(var(--bs-primary-rgb))] hover:text-white"
                                        onClick={() => onViewFilesClick(app.id)}
                                      >
                                        <Folder2 size={12} />
                                      </Button>

                                      {app.importedFrom && (
                                        <>
                                          <Button
                                            size="sm"
                                            className="px-1 py-0 mx-1 bg-primary-light text-black border-none hover:bg-[rgb(var(--bs-primary-rgb))] hover:text-white"
                                            onClick={() => onResetApartmentClick(app.id)}
                                          >
                                            <ArrowClockwise size={12} />
                                          </Button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                              {!collapsedApartments.includes(app.id) && (
                                <>
                                  {app.importedFrom?.driveFolder && (
                                    <Button
                                      size="sm"
                                      disabled={readOnlyMode}
                                      className="px-1 py-0 mx-1 bg-primary-light text-black border-none hover:bg-[rgb(var(--bs-primary-rgb))] hover:text-white"
                                      onClick={() => {
                                        let fileURL = `https://drive.google.com/drive/u/0/folders/${app.importedFrom.driveFolder}`;
                                        window.open(fileURL, '_blank');
                                      }}
                                    >
                                      <img src={GoogleDriveIcon} height={12} />
                                    </Button>
                                  )}
                                </>
                              )}
                              {appartments.length > 1 && !collapsedApartments.includes(app.id) && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  disabled={readOnlyMode}
                                  className="px-1 py-0 mx-1"
                                  onClick={() => onDeleteAppartmentClick(app.id)}
                                >
                                  <Trash size={12} />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
              )}
              <TableBody>
                {Object.keys(irrFields).map((sectionName, index) => (
                  <React.Fragment key={sectionName}>
                    <SectionRow
                      sectionName={sectionName}
                      span={apparmentsToShow.length + 1}
                      isRtlLanguage={isRtlLanguage}
                      type={'pinuiBinui'}
                    />
                    {irrFields[sectionName].map(rowDetails => (
                      <DataRow
                        {...rowDetails}
                        allFields={allFields}
                        appartments={apparmentsToShow}
                        onValueChange={onInputValueChange}
                        isRtlLanguage={isRtlLanguage}
                        collapsedApartments={collapsedApartments}
                        tabIndex={index + 1}
                        totalFieldsCount={totalFieldsCount}
                        readOnlyMode={readOnlyMode || selectMode}
                        allowNote={allowNote}
                        notes={notes}
                        onFieldInfoClick={onFieldInfoClick}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Card.Body>
        </Card>
      </Container>
      {customer && (
        <>
          <FileViewer
            show={viewingFilesOfAppartment !== null}
            onHide={() => {
              setViewingFilesOfAppartment(null);
              setFileUploadMeta(null);
            }}
            files={viewingFilesOfAppartment?.['files']}
            onFileDeleteClick={onFileDeleteClick}
            withFileUploader={Boolean(fileUploadModalMeta)}
            showProgress={fileUploadModalMeta?.showProgress}
            uploadedFiles={fileUploadModalMeta?.uploadedFiles}
            onFilesUpload={uploadedFiles => setFileUploadMeta({ ...fileUploadModalMeta, uploadedFiles })}
            onFileSaveClick={onFileSaveClick}
          />
          <AlertModal
            show={deleteFileModalMeta !== null}
            alertText={translate('are_you_sure_delete_file', {
              fileName: deleteFileModalMeta?.file?.metadata?.originalName || deleteFileModalMeta?.file?.filename || ''
            })}
            showProgress={deleteFileModalMeta && deleteFileModalMeta.deletingFile}
            progressText={`${translate('deleting_file')}...`}
            continueButtonText={translate('delete')}
            onDismissClick={() => setDeleteFileModalMeta(null)}
            onHide={() => setDeleteFileModalMeta(null)}
            onContinueClick={deleteFile}
          />
          <AlertModal
            show={Boolean(archiveAppartmentMeta)}
            alertText={translate('are_you_sure_archive')}
            showProgress={archiveAppartmentMeta?.showProgress}
            continueButtonText={translate('archive')}
            onDismissClick={() => setArchiveAppartmentMeta(null)}
            onHide={() => setArchiveAppartmentMeta(null)}
            onContinueClick={onArchiveAppartment}
          />
          <CustomerSelectModal
            show={Boolean(apartmentCopyModalMeta)}
            apartmentToCopy={apartmentCopyModalMeta?.apartmentToCopy}
            showProgress={apartmentCopyModalMeta?.showProgress}
            onHide={() => setApartmentCopyModalMeta(null)}
            currentCustomer={customer}
            selectedCustomer={apartmentCopyModalMeta?.selectedCustomer}
            onCustomerSelect={selectedCustomer => setApartmentCopyModalMeta(meta => ({ ...meta, selectedCustomer }))}
            onSubmit={onApartmentCopySubmit}
          />
        </>
      )}
    </>
  );
};

const PinuiBinuiIRR = ({
  customer,
  onCustomerUpdate,
  fromCustomerPortal,
  adminCompTable,
  readOnlyMode,
  selectMode,
  onSelectAppartment,
  addApartmentMode,
  onChange,
  assigneUsers,
  allowNote,
  notes,
  onFieldInfoClick,
  setCsvDataFields = () => {}
}) => {
  const [appartments, setAppartments] = useState([]);
  const [updatingAppartments, setUpdatingAppartments] = useState(false);
  const [collapsedApartments, setCollapsedApartments] = useState([]);
  const [apartmentCopyModalMeta, setApartmentCopyModalMeta] = useState(null);
  const [relatedApartments, setRelatedApartments] = useState([]);
  const [viewingFilesOfAppartment, setViewingFilesOfAppartment] = useState(null);
  const [fileUploadModalMeta, setFileUploadMeta] = useState(null);
  const [deleteFileModalMeta, setDeleteFileModalMeta] = useState(null);

  const { translate, langCode } = useContext(LocalizeContext);
  const [fullScreenView, setFullScreenView] = useState(false);
  const { user } = useAuth();
  const calculators = useAppChoices('calculators');
  const calculatorConfig = useMemo(() => calculators?.find(c => c.type === 'pinuiBinui'), [calculators]);
  const apparmentsToShow = useMemo(() => appartments.filter(a => addApartmentMode || !a.isArchived), [appartments]);

  const irrFields = useMemo(
    () =>
      getPinuiBinuiIrrFields({
        fieldsFromServer: calculatorConfig?.fields,
        lang: langCode?.toLowerCase(),
        addApartmentMode,
        users: assigneUsers,
        loggedInUser: user,
        translate
      }),
    [langCode, calculatorConfig]
  );

  const totalFieldsCount = useMemo(
    () => Object.keys(irrFields).reduce((acc, sectionName) => irrFields[sectionName].length + acc, 0),
    [irrFields]
  );

  const allFields = useMemo(() => Object.keys(irrFields).flatMap(sectionName => irrFields[sectionName]), [irrFields]);

  const isRtlLanguage = useMemo(() => isRtl(langCode), [langCode]);

  const tableContainerRef = useRef();
  const [newApartmentAdded, setNewApartmentAdded] = useState(false);
  const [updatedApartment, setUpdatedApartment] = useState('all');

  useEffect(() => {
    let initialAppartment =
      customer?.pinuiBinuiIrrAppartments?.length > 0
        ? customer?.pinuiBinuiIrrAppartments.map(a => {
            const baseValues = a._id ? a : { ...getInitialValues(allFields, adminCompTable), ...a };
            return {
              ...baseValues,
              id: uniqueId(),
              ...getCalculatedValues(a),
              ...getPercentageValues(a, allFields)
            };
          })
        : [getInitialValues(allFields, adminCompTable)];

    setAppartments(initialAppartment);
    setCollapsedApartments(
      addApartmentMode ? [] : initialAppartment?.filter(appartment => appartment?.isCollapsed)?.map(a => a?.id)
    );
  }, [customer]);

  const fetchRelatedApartments = async () => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.GET_APPARTMENTS,
      requestBody: {
        relatedToCustomer: customer?._id,
        filter: [{ key: 'type', value: 'pinui-binui' }]
      }
    });

    if (response) {
      setRelatedApartments(response.results);
    }
  };

  useEffect(() => {
    if (customer && !selectMode && !addApartmentMode && !readOnlyMode) fetchRelatedApartments();
  }, []);

  const onInputValueChange = (appId, objectKey, currentInputType, newValue, otherInfo) => {
    const apartment = appartments.find(a => a.id === appId);
    updateApartmentValues(allFields, apartment, objectKey, currentInputType, newValue, otherInfo);
    setAppartments(appartments.map(app => (app.id === appId ? { ...app, ...getCalculatedValues(app) } : app)));
    setUpdatedApartment(appId);
  };

  const getCalculatedValues = apartment => {
    const parsedValues = getParsedValues(allFields, apartment, adminCompTable);
    return combinedIRRFormula(parsedValues.inputPrice, {
      ...parsedValues,
      useNewIRR: true
    });
  };

  useDebouncedEffect(
    () => {
      if (!updatedApartment || !appartments?.length || !addApartmentMode) return;
      setUpdatedApartment(); // prevent infinite loop

      const recalculateTargetPrices = async () => {
        const shouldUpdate = apartment => updatedApartment === 'all' || updatedApartment === apartment.id;

        const getUpdatedApartment = async apartment => {
          if (!shouldUpdate(apartment)) return apartment;
          if (!apartment.targetIrr) return { ...apartment, targetPrice: 0 };

          const parsedValues = getParsedValues(allFields, apartment, adminCompTable);
          const targetIRRNum = parsedValues.targetIrr;

          return new Promise(resolve => {
            setTimeout(() => {
              const targetPrice = computeTargetPrice(parsedValues.inputPrice, targetIRRNum, {
                ...parsedValues,
                useNewIRR: true
              });
              resolve({ ...apartment, targetPrice });
            }, 0);
          });
        };

        const updatedList = await Promise.all(appartments.map(getUpdatedApartment));
        setAppartments(updatedList);
      };

      recalculateTargetPrices();
    },
    [appartments, updatedApartment],
    100
  );

  useEffect(() => {
    if (newApartmentAdded) {
      scrollToEnd(tableContainerRef.current.firstChild);
      setNewApartmentAdded(false); // Reset the flag
    }
  }, [appartments, newApartmentAdded]); // Effect runs when apartments or newApartmentAdded changes

  const onAddAppartmentClick = () => {
    appartments.push(getInitialValues(allFields, adminCompTable));
    setAppartments([...appartments]);
    setNewApartmentAdded(true);
  };

  const onDeleteAppartmentClick = appId => {
    appartments.splice(
      appartments.findIndex(a => a.id === appId),
      1
    );
    setAppartments([...appartments]);
  };

  const onSaveAppartmentsClick = async silentSave => {
    if (!silentSave) {
      toast.info('Updating appartments...');
      setUpdatingAppartments(true);
    }

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        pinuiBinuiIrrAppartments: appartments.map(appartment =>
          getServerFieldsOfAppartments(
            { ...appartment, ...getParsedValues(allFields, appartment, adminCompTable, false) },
            collapsedApartments?.includes(appartment.id),
            appartment.isArchived
          )
        ),
        _id: customer['_id']
      }
    });

    setUpdatingAppartments(false);
    if (error) {
      if (!silentSave) toast.error(error);
      return;
    }

    if (!silentSave) toast.success('Updated successfully!');
    onCustomerUpdate(response);
  };

  const [archiveAppartmentMeta, setArchiveAppartmentMeta] = useState(null);

  const onArchiveAppartmentClick = appartment => {
    setArchiveAppartmentMeta({ appartment });
  };

  const onArchiveAppartment = async () => {
    try {
      let { appartment } = archiveAppartmentMeta;
      setArchiveAppartmentMeta({ ...archiveAppartmentMeta, showProgress: true });
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.USERS_UPDATE,
        requestBody: {
          pinuiBinuiIrrAppartments: appartments.map(app =>
            getServerFieldsOfAppartments(
              { ...app, ...getParsedValues(allFields, app, adminCompTable, false) },
              collapsedApartments?.includes(app.id),
              app.id !== appartment?.id ? app.isArchived : true
            )
          ),
          _id: customer['_id']
        }
      });

      if (error) {
        toast.error(error);
        return;
      }
      if (response) {
        toast.success('Archived successfully!');
        onCustomerUpdate(response);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setArchiveAppartmentMeta(null);
    }
  };

  const onViewFilesClick = appId => {
    setViewingFilesOfAppartment(appartments.find(a => a.id === appId));
    setFileUploadMeta({ appId });
  };

  const onFileDeleteClick = file => {
    setDeleteFileModalMeta({ file });
  };

  const onResetApartmentClick = appId => {
    const linkedApartment = { ...appartments.find(a => a.id === appId)?.importedFrom };
    ['_id', 'importedFrom', 'id'].forEach(key => {
      delete linkedApartment[key];
    });

    if (linkedApartment) {
      setAppartments(
        appartments.map(a => {
          if (a.id === appId) {
            const resetApartment = { ...a, ...linkedApartment };
            return { ...resetApartment, ...getCalculatedValues(resetApartment) };
          }

          return a;
        })
      );
      setUpdatedApartment(appId);
    }
  };

  const deleteFile = async () => {
    const { file: toBeDeletedFile } = deleteFileModalMeta;
    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: true });

    const newFileArray = viewingFilesOfAppartment.files.filter(f => f._id !== toBeDeletedFile._id);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        irrAppartments: appartments.map(app => ({
          ...getServerFieldsOfAppartments(
            {
              ...app,
              ...getParsedValues(allFields, app, adminCompTable, false)
            },
            collapsedApartments?.includes(app.id),
            app.isArchived
          ),
          files: app.id !== viewingFilesOfAppartment.id ? app.files.map(f => f._id) : newFileArray.map(f => f._id)
        })),
        _id: customer['_id']
      }
    });

    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: false });
    if (error) {
      return toast.error(error);
    }

    setViewingFilesOfAppartment({ ...viewingFilesOfAppartment, files: newFileArray });

    setDeleteFileModalMeta(null);
    onCustomerUpdate(response);
    deleteFileFromServer(toBeDeletedFile);
  };

  const onFileSaveClick = async () => {
    const { uploadedFiles, appId } = fileUploadModalMeta;
    if (uploadedFiles.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }

    if (uploadedFiles.some(f => f.status !== 'UPLOADED')) {
      return toast.error('Some of the files are still uploading to the server, please try again in a moment!');
    }

    setFileUploadMeta({ ...fileUploadModalMeta, showProgress: true });

    const oldFiles = appartments.find(a => a.id === appId).files;
    const newFiles = uploadedFiles.map(f => ({ ...f, _id: f.id }));

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        irrAppartments: appartments.map(app => ({
          ...getServerFieldsOfAppartments(
            { ...app, ...getParsedValues(allFields, app, adminCompTable, false) },
            collapsedApartments?.includes(app.id),
            app.isArchived
          ),
          files: app.id !== appId ? app.files.map(f => f._id) : [...oldFiles, ...newFiles].map(f => f._id)
        })),
        _id: customer['_id']
      }
    });

    setFileUploadMeta({ ...fileUploadModalMeta, showProgress: false });

    if (error) {
      return toast.error(error);
    }

    setFileUploadMeta({ ...fileUploadModalMeta, uploadedFiles: [] });
    setViewingFilesOfAppartment({ ...viewingFilesOfAppartment, files: [...oldFiles, ...newFiles] });
    onCustomerUpdate(response);
  };

  const onCollapseApartmentClick = appId => {
    if (collapsedApartments.includes(appId)) {
      collapsedApartments.splice(collapsedApartments.indexOf(appId), 1);
    } else {
      collapsedApartments.push(appId);
    }

    setCollapsedApartments([...collapsedApartments]);
  };

  const onCopyApartmentClick = appId => {
    onSaveAppartmentsClick(true);
    const apartmentToCopy = appartments.find(a => a.id === appId);
    setApartmentCopyModalMeta({ apartmentToCopy });
  };

  const onApartmentCopySubmit = async () => {
    const { apartmentToCopy, selectedCustomer } = apartmentCopyModalMeta;

    toast.info('Updating appartments...');
    setApartmentCopyModalMeta(meta => ({ ...meta, showProgress: true }));

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_COPY_APARTMENT,
      requestBody: {
        apartmentCalculatorKey: 'pinuiBinuiIrrAppartments',
        apartment: getServerFieldsOfAppartments({
          ...apartmentToCopy,
          ...getParsedValues(allFields, apartmentToCopy, adminCompTable, false)
        }),
        _id: selectedCustomer['_id']
      }
    });

    setApartmentCopyModalMeta(meta => ({ ...meta, showProgress: false }));

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Copied successfully!');
    setApartmentCopyModalMeta(null);
    onCustomerUpdate(response);
  };

  const handleSelectAppartment = apartment => {
    const parsedValues = getParsedValues(allFields, apartment, adminCompTable);
    onSelectAppartment({ ...parsedValues, ...combinedIRRFormula(parsedValues.inputPrice, parsedValues) });
  };

  useEffect(() => {
    if (addApartmentMode && onChange) {
      const apartmentToReturn = appartments[0] || {};
      onChange({ ...apartmentToReturn, ...getParsedValues(allFields, apartmentToReturn, adminCompTable, false) });
    }
  }, [onChange, addApartmentMode, allFields, adminCompTable, appartments]);

  const onImportApartment = app => {
    const newApartment = {
      ...app,
      id: uniqueId(),
      ...getCalculatedValues(app),
      ...getPercentageValues(app, allFields),
      importedFrom: app
    };
    appartments.push(newApartment);
    setAppartments([...appartments]);
    setNewApartmentAdded(true);
    setUpdatedApartment(newApartment.id);
  };

  const showImportApartment = useMemo(
    () =>
      !addApartmentMode &&
      !selectMode &&
      !readOnlyMode &&
      customer &&
      relatedApartments.filter(app => !appartments.map(a => a.importedFrom?._id).includes(app._id)).length > 0,
    [addApartmentMode, selectMode, readOnlyMode, customer, relatedApartments, appartments]
  );

  const calculatorHeight = useMemo(() => {
    if (addApartmentMode) return '100%';
    if (fullScreenView) {
      return showImportApartment ? 'calc(100vh - 1.625rem - 60px)' : 'calc(100vh - 3.5rem)';
    }
    if (!customer || selectMode) return '80vh';
    return showImportApartment ? 'calc(65vh - 60px)' : 'calc(100vh - 230px';
  }, [addApartmentMode, showImportApartment, customer, selectMode, fullScreenView]);

  useEffect(() => {
    if (!allFields || !apparmentsToShow) return;
    setCsvDataFields({
      fields: [...allFields],
      values: apparmentsToShow.find(a => a._id === customer?.pinuiBinuiIrrAppartments?.[0]?._id)
    });
  }, [allFields, apparmentsToShow]);

  if (fullScreenView) {
    return (
      <FullscreenModal show={fullScreenView} onHide={() => setFullScreenView(false)}>
        <Calculator
          fullScreenView={fullScreenView}
          setFullScreenView={setFullScreenView}
          showImportApartment={showImportApartment}
          relatedApartments={relatedApartments}
          addApartmentMode={addApartmentMode}
          selectMode={selectMode}
          appartments={appartments}
          onImportApartment={onImportApartment}
          isRtlLanguage={isRtlLanguage}
          translate={translate}
          readOnlyMode={readOnlyMode}
          updatingAppartments={updatingAppartments}
          onAddAppartmentClick={onAddAppartmentClick}
          customer={customer}
          onSaveAppartmentsClick={onSaveAppartmentsClick}
          tableContainerRef={tableContainerRef}
          calculatorHeight={calculatorHeight}
          apparmentsToShow={apparmentsToShow}
          collapsedApartments={collapsedApartments}
          totalFieldsCount={totalFieldsCount}
          onInputValueChange={onInputValueChange}
          onCollapseApartmentClick={onCollapseApartmentClick}
          handleSelectAppartment={handleSelectAppartment}
          onDeleteAppartmentClick={onDeleteAppartmentClick}
          onArchiveAppartmentClick={onArchiveAppartmentClick}
          onCopyApartmentClick={onCopyApartmentClick}
          onResetApartmentClick={onResetApartmentClick}
          irrFields={irrFields}
          allFields={allFields}
          archiveAppartmentMeta={archiveAppartmentMeta}
          setArchiveAppartmentMeta={setArchiveAppartmentMeta}
          onArchiveAppartment={onArchiveAppartment}
          apartmentCopyModalMeta={apartmentCopyModalMeta}
          setApartmentCopyModalMeta={setApartmentCopyModalMeta}
          onApartmentCopySubmit={onApartmentCopySubmit}
          onViewFilesClick={onViewFilesClick}
          viewingFilesOfAppartment={viewingFilesOfAppartment}
          setViewingFilesOfAppartment={setViewingFilesOfAppartment}
          fileUploadModalMeta={fileUploadModalMeta}
          setFileUploadMeta={setFileUploadMeta}
          deleteFileModalMeta={deleteFileModalMeta}
          setDeleteFileModalMeta={setDeleteFileModalMeta}
          deleteFile={deleteFile}
          onFileDeleteClick={onFileDeleteClick}
          onFileSaveClick={onFileSaveClick}
          allowNote={allowNote}
          notes={notes}
          onFieldInfoClick={onFieldInfoClick}
          user={user}
        />
      </FullscreenModal>
    );
  }

  return (
    <Calculator
      fullScreenView={fullScreenView}
      setFullScreenView={setFullScreenView}
      showImportApartment={showImportApartment}
      relatedApartments={relatedApartments}
      addApartmentMode={addApartmentMode}
      selectMode={selectMode}
      appartments={appartments}
      onImportApartment={onImportApartment}
      isRtlLanguage={isRtlLanguage}
      translate={translate}
      readOnlyMode={readOnlyMode}
      updatingAppartments={updatingAppartments}
      onAddAppartmentClick={onAddAppartmentClick}
      customer={customer}
      onSaveAppartmentsClick={onSaveAppartmentsClick}
      tableContainerRef={tableContainerRef}
      calculatorHeight={calculatorHeight}
      apparmentsToShow={apparmentsToShow}
      collapsedApartments={collapsedApartments}
      totalFieldsCount={totalFieldsCount}
      onInputValueChange={onInputValueChange}
      onCollapseApartmentClick={onCollapseApartmentClick}
      handleSelectAppartment={handleSelectAppartment}
      onDeleteAppartmentClick={onDeleteAppartmentClick}
      onArchiveAppartmentClick={onArchiveAppartmentClick}
      onCopyApartmentClick={onCopyApartmentClick}
      onResetApartmentClick={onResetApartmentClick}
      irrFields={irrFields}
      allFields={allFields}
      archiveAppartmentMeta={archiveAppartmentMeta}
      setArchiveAppartmentMeta={setArchiveAppartmentMeta}
      onArchiveAppartment={onArchiveAppartment}
      apartmentCopyModalMeta={apartmentCopyModalMeta}
      setApartmentCopyModalMeta={setApartmentCopyModalMeta}
      onApartmentCopySubmit={onApartmentCopySubmit}
      onViewFilesClick={onViewFilesClick}
      viewingFilesOfAppartment={viewingFilesOfAppartment}
      setViewingFilesOfAppartment={setViewingFilesOfAppartment}
      fileUploadModalMeta={fileUploadModalMeta}
      setFileUploadMeta={setFileUploadMeta}
      deleteFileModalMeta={deleteFileModalMeta}
      setDeleteFileModalMeta={setDeleteFileModalMeta}
      deleteFile={deleteFile}
      onFileDeleteClick={onFileDeleteClick}
      onFileSaveClick={onFileSaveClick}
      allowNote={allowNote}
      notes={notes}
      onFieldInfoClick={onFieldInfoClick}
      user={user}
    />
  );
};

export default PinuiBinuiIRR;
